import { state, shortUUID } from '@codesuma/baseline'
import { IDayCell } from './calendar'
import { IEvent, IOccurrence, occurrencesInRange } from './event-repeat'
import { IPhoneEvent, UNTITLED, dayKey, indexByDay } from './phone-events'
import { nextSeq, sortBySeq } from './storage/rows'
import { collection, fellBack } from './storage'
import { ICustomOccasion } from './occasion-repeat'
import { migrateOccasions } from './occasion-event'

// The store for Emrouz's own events: persisted through the storage repository
// and broadcast through the baseline state bus, so state.on('events', handler)
// redraws whatever is showing them. The same shape as the occasion and task
// stores.
//
// When an event happens is not decided here — see services/event-repeat.ts for
// that rule and for the event's own shape.

export type { EventFreq, IEventRepeat, IEvent } from './event-repeat'

const KEY = 'events'
const repo = collection<IEvent>(KEY)
let all: IEvent[] = []

export async function init() {
    const legacy = collection<ICustomOccasion>('custom-occasions')
    // A session that fell back reads the legacy rows off the pre-migration
    // snapshot, which is never deleted. Migrating those for real would put
    // every old occasion into the pending delta, and the next healthy launch
    // would replay it over rows the user has edited since. So there the rows
    // are migrated for display only and nothing is written: the real store
    // already holds them, or will migrate them on the next healthy launch.
    const storage = fellBack()
        ? { save: async () => {}, removeLegacy: async () => {} }
        : { save: repo.put, removeLegacy: legacy.remove }
    all = sortBySeq(await migrateOccasions(await repo.load(), await legacy.load(), storage))
}

// Expanded occurrences by day, one entry per Gregorian month, built when a
// month is first asked for and dropped whole whenever the store changes. The
// month grid asks for one day at a time, and expanding every event over every
// cell would do the same work forty-two times over.
const months = new Map<string, Record<string, IPhoneEvent[]>>()

// An occurrence, in the shape the day page and the month fold already draw the
// phone's events in — so one set of row components serves both sources.
const shape = (e: IEvent, occ: IOccurrence): IPhoneEvent => ({
    id: e.id,
    title: e.title.trim() || UNTITLED,
    allDay: e.allDay,
    start: occ.start,
    end: occ.end,
    // Ours, so there is no phone calendar behind it to name.
    calendar: '',
    eventId: e.id,
})

function monthIndex(gy: number, gm: number): Record<string, IPhoneEvent[]> {
    const key = `${gy}-${gm}`
    const hit = months.get(key)
    if (hit) return hit

    // Local midnight to local midnight, the month where the phone is.
    const from = new Date(gy, gm - 1, 1).getTime()
    const to = new Date(gy, gm, 1).getTime()
    const rows: IPhoneEvent[] = []
    for (const e of all) {
        // Occasions use the calendar-specific day view, backed by this same collection.
        if (e.kind === 'occasion') continue
        for (const occ of occurrencesInRange(e, from, to)) rows.push(shape(e, occ))
    }
    const built = indexByDay(rows)
    months.set(key, built)
    return built
}

function forDay(cell: IDayCell): IPhoneEvent[] {
    return monthIndex(cell.gy, cell.gm)[dayKey(cell.gy, cell.gm, cell.gd)] || []
}

function broadcast() {
    months.clear()
    state.set(KEY, all)
    state.set('custom-occasions', all.filter(e => e.kind === 'occasion'))
}

// The phone's own ids of the copies older versions wrote into its calendars.
// The reader drops these, so an event that was written to both places is still
// only drawn once on its day.
function publishedIds(): string[] {
    const out: string[] = []
    for (const e of all) {
        for (const c of e.published || []) out.push(c.id)
    }
    return out
}

function byId(id: string): IEvent | undefined {
    return all.find(e => e.id === id)
}

function add(event: Omit<IEvent, 'id'>): IEvent {
    const saved = { ...event, id: shortUUID(), seq: nextSeq(all) }
    all = [...all, saved]
    void repo.put(saved)
    broadcast()
    return saved
}

function update(id: string, patch: Partial<Omit<IEvent, 'id'>>) {
    let saved: IEvent | undefined
    all = all.map(e => {
        if (e.id !== id) return e
        saved = { ...e, ...patch }
        return saved
    })
    if (saved) void repo.put(saved)
    broadcast()
}

function remove(id: string) {
    all = all.filter(e => e.id !== id)
    void repo.remove(id)
    broadcast()
}

export default { init, forDay, byId, publishedIds, all: () => all, add, update, remove }
